import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Dialog, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import {
  Close as CloseIcon,
  DownloadOutlined as DownloadIcon,
  FitScreenOutlined as FitIcon,
  ZoomInOutlined as ZoomInIcon,
  ZoomOutOutlined as ZoomOutIcon,
} from '@mui/icons-material';
import type { DeliveryEvidenceFile } from '../../types/delivery.type';

interface Props {
  photo: (DeliveryEvidenceFile & { title?: string }) | null;
  onClose: () => void;
}

const ZOOM_STEPS = [1, 1.5, 2, 3, 4];

/** Full-size evidence photo: zoom in/out (buttons or wheel), pan when zoomed, download. */
export default function PhotoLightbox({ photo, onClose }: Props) {
  const { t } = useTranslation();
  const [zoom, setZoom] = useState(1);

  useEffect(() => { setZoom(1); }, [photo?.asset_id]);

  const stepZoom = (direction: 1 | -1) => {
    const index = ZOOM_STEPS.indexOf(zoom);
    const next = ZOOM_STEPS[Math.min(ZOOM_STEPS.length - 1, Math.max(0, (index === -1 ? 0 : index) + direction))];
    setZoom(next);
  };

  return (
    <Dialog open={Boolean(photo)} onClose={onClose} fullWidth maxWidth="lg" PaperProps={{ sx: { bgcolor: '#111', color: '#fff' } }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1 }}>
        <Typography variant="subtitle2" sx={{ flex: 1 }} noWrap>
          {photo?.title ?? photo?.filename ?? ''}
        </Typography>
        <Tooltip title={t('lightbox.zoomOut')}><span><IconButton color="inherit" onClick={() => stepZoom(-1)} disabled={zoom === ZOOM_STEPS[0]}><ZoomOutIcon /></IconButton></span></Tooltip>
        <Typography variant="caption" sx={{ minWidth: 40, textAlign: 'center' }}>{Math.round(zoom * 100)}%</Typography>
        <Tooltip title={t('lightbox.zoomIn')}><span><IconButton color="inherit" onClick={() => stepZoom(1)} disabled={zoom === ZOOM_STEPS[ZOOM_STEPS.length - 1]}><ZoomInIcon /></IconButton></span></Tooltip>
        <Tooltip title={t('lightbox.fit')}><IconButton color="inherit" onClick={() => setZoom(1)}><FitIcon /></IconButton></Tooltip>
        {photo?.download_url ? (
          <Tooltip title={t('lightbox.download')}>
            <IconButton color="inherit" component="a" href={photo.download_url} download={photo.filename ?? photo.asset_id} target="_blank" rel="noopener noreferrer">
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        ) : null}
        <IconButton color="inherit" onClick={onClose} aria-label={t('lightbox.close')}><CloseIcon /></IconButton>
      </Stack>
      <Box
        sx={{ overflow: 'auto', maxHeight: '80vh', display: 'flex', justifyContent: zoom === 1 ? 'center' : 'flex-start', alignItems: 'flex-start', bgcolor: '#000' }}
        onWheel={(event) => {
          if (!event.ctrlKey && !event.metaKey) return;
          event.preventDefault();
          stepZoom(event.deltaY < 0 ? 1 : -1);
        }}
      >
        {photo?.download_url ? (
          <Box
            component="img"
            src={photo.download_url}
            alt={photo.filename ?? 'evidence'}
            sx={{
              display: 'block',
              maxWidth: zoom === 1 ? '100%' : 'none',
              maxHeight: zoom === 1 ? '80vh' : 'none',
              width: zoom === 1 ? 'auto' : `${zoom * 100}%`,
              cursor: zoom === 1 ? 'zoom-in' : 'zoom-out',
              transition: 'width 120ms ease',
            }}
            onClick={() => setZoom(zoom === 1 ? 2 : 1)}
          />
        ) : null}
      </Box>
    </Dialog>
  );
}
